import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Cost, CostsState } from './types';

const initialState: CostsState = {
  locations: {}
};

export const costsSlice = createSlice({
  name: 'costs',
  initialState,
  reducers: {
    // Initialiser les coûts d'un emplacement avec une commission de base par défaut
    // seulement si aucun coût n'existe déjà pour cet emplacement
    initializeLocationCosts: (state, action: PayloadAction<string>) => {
      const locationId = action.payload;
      // Vérifier si des coûts existent déjà pour cet emplacement
      if (!state.locations[locationId] || state.locations[locationId].length === 0) {
        state.locations[locationId] = [
          {
            id: '1',
            name: 'Commission Grand Magasin',
            type: 'percentage',
            value: 0,
            isBaseCommission: true,
          }
        ];
      }
    },
    
    // Ajouter un coût à un emplacement
    addCost: (state, action: PayloadAction<{ locationId: string; cost: Cost }>) => {
      const { locationId, cost } = action.payload;
      if (!state.locations[locationId]) {
        state.locations[locationId] = [];
      }
      state.locations[locationId].push(cost);
    },
    
    // Mettre à jour un coût existant
    updateCost: (state, action: PayloadAction<{ 
      locationId: string; 
      costId: string; 
      field: 'name' | 'type' | 'value'; 
      value: string | number 
    }>) => {
      const { locationId, costId, field, value } = action.payload;
      const costs = state.locations[locationId];
      if (costs) {
        const costIndex = costs.findIndex(cost => cost.id === costId);
        if (costIndex !== -1) {
          if (field === 'type') {
            // Si c'est la commission de base, on ne change pas son type
            if (costs[costIndex].isBaseCommission) {
              return;
            }
            costs[costIndex] = { ...costs[costIndex], [field]: value as 'percentage' | 'fixed' };
          } else if (field === 'value') {
            costs[costIndex] = { ...costs[costIndex], [field]: Number(value) };
          } else if (field === 'name') {
            costs[costIndex] = { ...costs[costIndex], [field]: String(value) };
          }
        }
      }
    },
    
    // Supprimer un coût
    removeCost: (state, action: PayloadAction<{ locationId: string; costId: string }>) => {
      const { locationId, costId } = action.payload;
      const costs = state.locations[locationId];
      if (costs) {
        const costToRemove = costs.find(cost => cost.id === costId);
        if (costToRemove?.isBaseCommission) {
          // Ne pas supprimer la commission de base, mais mettre sa valeur à 0
          const costIndex = costs.findIndex(cost => cost.id === costId);
          if (costIndex !== -1) {
            costs[costIndex] = { ...costs[costIndex], value: 0 };
          }
        } else {
          // Supprimer normalement les autres coûts
          state.locations[locationId] = costs.filter(cost => cost.id !== costId);
        }
      }
    },
    
    // Définir tous les coûts d'un emplacement (utile pour l'initialisation)
    setLocationCosts: (state, action: PayloadAction<{ locationId: string; costs: Cost[] }>) => {
      const { locationId, costs } = action.payload;
      state.locations[locationId] = costs;
    }
  }
});

export const { 
  initializeLocationCosts, 
  addCost, 
  updateCost, 
  removeCost, 
  setLocationCosts 
} = costsSlice.actions;

export default costsSlice.reducer;
